// src/data/schemas/Shelter.js
// Emergency shelters, cooling centers, warming centers, feeding sites.
// Firestore collection: shelters

import { uuid, nowISO, isLat, isLon, isISO, isIn, isPos, runValidation, toFirestoreClean, fromFirestoreNormalize } from './shared';

export const SHELTER_STATUS   = ['open', 'at_capacity', 'closed', 'pending', 'unknown'];
export const SHELTER_TYPE     = [
  'general',          // general population hurricane shelter
  'special_needs',    // medical needs, power-dependent equipment
  'pet_friendly',     // accepts pets
  'cooling_center',   // heat emergency
  'warming_center',   // cold emergency
  'feeding_site',     // food / water distribution only
  'medical_aid',      // first aid / triage
  'transit_hub',      // evacuation pickup point
];
export const SHELTER_OPERATOR = [
  'red_cross',
  'salvation_army',
  'fema',
  'state',
  'parish',
  'municipal',
  'faith_based',
  'private',
  'other',
];

export const SCHEMA_SHELTER = {
  id:               { required: true,  type: 'string' },
  name:             { required: true,  type: 'string',  maxLen: 200 },
  type:             { required: true,  type: 'string',  check: (v) => isIn(v, SHELTER_TYPE),   message: `one of: ${SHELTER_TYPE.join(', ')}` },
  status:           { required: true,  type: 'string',  check: (v) => isIn(v, SHELTER_STATUS), message: `one of: ${SHELTER_STATUS.join(', ')}` },
  operator:         { required: false, type: 'string',  check: (v) => isIn(v, SHELTER_OPERATOR) },
  address:          { required: true,  type: 'string',  maxLen: 300 },
  parish:           { required: true,  type: 'string',  maxLen: 100 },
  lat:              { required: true,  type: 'number',  check: isLat, message: 'valid latitude' },
  lon:              { required: true,  type: 'number',  check: isLon, message: 'valid longitude' },
  // Capacity
  capacityTotal:    { required: false, type: 'number',  min: 0 },
  capacityCurrent:  { required: false, type: 'number',  min: 0 },   // current occupancy
  // Accessibility
  isADAAccessible:  { required: false, type: 'boolean' },
  acceptsPets:      { required: false, type: 'boolean' },
  acceptsSpecialNeeds: { required: false, type: 'boolean' },
  hasMedicalStaff:  { required: false, type: 'boolean' },
  hasFood:          { required: false, type: 'boolean' },
  hasWater:         { required: false, type: 'boolean' },
  hasCots:          { required: false, type: 'boolean' },
  hasAC:            { required: false, type: 'boolean' },
  hasParking:       { required: false, type: 'boolean' },
  hasBackupPower:   { required: false, type: 'boolean' },
  // Contact
  phone:            { required: false, type: 'string',  maxLen: 20 },
  // Hours
  openedAt:         { required: false, type: 'string',  check: isISO, message: 'valid ISO datetime' },
  closedAt:         { required: false, type: 'string',  check: isISO, message: 'valid ISO datetime' },
  hours:            { required: false, type: 'string',  maxLen: 100 },   // "Open 24/7" etc
  // Notes
  notes:            { required: false, type: 'string',  maxLen: 500 },
  // Tracking
  reportedAt:       { required: true,  type: 'string',  check: isISO, message: 'valid ISO datetime' },
  updatedAt:        { required: false, type: 'string',  check: isISO, message: 'valid ISO datetime' },
  reportedBy:       { required: false, type: 'string',  maxLen: 100 },
  // Confirmation
  confirmCount:     { required: false, type: 'number',  min: 0 },
  isOfficial:       { required: false, type: 'boolean' },    // from Red Cross / GOHSEP feed
  officialSourceId: { required: false, type: 'string'  },
};

export function createShelter(overrides = {}) {
  return {
    id:                  uuid(),
    name:                '',
    type:                'general',
    status:              'unknown',
    operator:            'other',
    address:             '',
    parish:              '',
    lat:                 null,
    lon:                 null,
    capacityTotal:       null,
    capacityCurrent:     null,
    isADAAccessible:     false,
    acceptsPets:         false,
    acceptsSpecialNeeds: false,
    hasMedicalStaff:     false,
    hasFood:             false,
    hasWater:            false,
    hasCots:             false,
    hasAC:               false,
    hasParking:          false,
    hasBackupPower:      false,
    phone:               null,
    openedAt:            null,
    closedAt:            null,
    hours:               null,
    notes:               null,
    reportedAt:          nowISO(),
    updatedAt:           null,
    reportedBy:          null,
    confirmCount:        0,
    isOfficial:          false,
    officialSourceId:    null,
    ...overrides,
  };
}

export function validateShelter(obj) {
  const result = runValidation(obj, SCHEMA_SHELTER);
  // Cross-field: at_capacity requires capacityTotal
  if (obj.status === 'at_capacity' && !obj.capacityTotal) {
    result.errors.push('capacityTotal: required when status is at_capacity');
    result.valid = false;
  }
  return result;
}

// Compute availability percentage (null if unknown)
export function shelterAvailability(shelter) {
  if (!shelter.capacityTotal || !shelter.capacityCurrent) return null;
  const used = shelter.capacityCurrent / shelter.capacityTotal;
  return Math.max(0, Math.min(1, 1 - used)); // 0 = full, 1 = empty
}

// Derive status label for display
export function shelterStatusLabel(shelter, lang = 'en') {
  const labels = {
    open:        { en: 'Open',       es: 'Abierto' },
    at_capacity: { en: 'Full',       es: 'Lleno' },
    closed:      { en: 'Closed',     es: 'Cerrado' },
    pending:     { en: 'Opening soon',es: 'Abriendo pronto' },
    unknown:     { en: 'Status unknown', es: 'Estado desconocido' },
  };
  return labels[shelter.status]?.[lang] ?? shelter.status;
}

// Seed data: known New Orleans area emergency shelters
export const SEED_SHELTERS = [
  createShelter({
    id: 'shelter-smoothie-king', name: 'Smoothie King Center',
    type: 'general', status: 'open', operator: 'municipal',
    address: '1501 Dave Dixon Dr, New Orleans, LA 70113',
    parish: 'Orleans', lat: 29.9489, lon: -90.0819,
    capacityTotal: 14000, hasFood: true, hasWater: true, hasCots: true,
    hasAC: true, hasParking: true, hasBackupPower: true,
    isADAAccessible: true, isOfficial: true,
    phone: '5045874000', notes: 'Primary Orleans Parish mega-shelter.',
    reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-alario-center', name: 'Alario Center',
    type: 'general', status: 'open', operator: 'parish',
    address: '2000 Segnette Blvd, Westwego, LA 70094',
    parish: 'Jefferson', lat: 29.9052, lon: -90.1443,
    capacityTotal: 3000, hasFood: true, hasWater: true, hasCots: true,
    hasAC: true, hasParking: true, acceptsPets: false, isADAAccessible: true,
    isOfficial: true, phone: '5043491300', reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-lamar-dixon', name: 'Lamar-Dixon Expo Center (Pet Shelter)',
    type: 'pet_friendly', status: 'open', operator: 'state',
    address: '9039 St Landry Rd, Gonzales, LA 70737',
    parish: 'Ascension', lat: 30.2060, lon: -90.9107,
    capacityTotal: 500, acceptsPets: true, hasFood: true, hasWater: true,
    isOfficial: true, notes: 'Primary pet-friendly shelter for the metro area.',
    reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-carver-hs', name: 'Carver High School',
    type: 'general', status: 'unknown', operator: 'municipal',
    address: '3059 Higgins Blvd, New Orleans, LA 70126',
    parish: 'Orleans', lat: 30.0037, lon: -89.9908,
    capacityTotal: 500, hasFood: false, hasWater: true, hasCots: true,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-st-tammany-fairgrounds', name: 'St. Tammany Parish Fairgrounds',
    type: 'general', status: 'unknown', operator: 'parish',
    address: '1901 Fremaux Ave, Slidell, LA 70460',
    parish: 'St. Tammany', lat: 30.328, lon: -89.786,
    capacityTotal: 2000, hasFood: false, hasWater: true, hasCots: true,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-covington-community', name: 'Covington Trailhead Community Center',
    type: 'general', status: 'unknown', operator: 'municipal',
    address: '419 N. New Hampshire St, Covington, LA 70433',
    parish: 'St. Tammany', lat: 30.476, lon: -90.101,
    capacityTotal: 500, hasFood: false, hasWater: true, hasCots: false,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-destrehan-hs', name: 'Destrehan High School',
    type: 'general', status: 'unknown', operator: 'parish',
    address: '145 Wildcat Dr, Destrehan, LA 70047',
    parish: 'St. Charles', lat: 29.943, lon: -90.356,
    capacityTotal: 1500, hasFood: false, hasWater: true, hasCots: true,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-st-bernard-civic', name: 'St. Bernard Parish Civic Auditorium',
    type: 'general', status: 'unknown', operator: 'parish',
    address: '8201 W. Judge Perez Dr, Chalmette, LA 70043',
    parish: 'St. Bernard', lat: 29.940, lon: -89.978,
    capacityTotal: 1000, hasFood: false, hasWater: true, hasCots: true,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-belle-chasse-hs', name: 'Belle Chasse High School',
    type: 'general', status: 'unknown', operator: 'parish',
    address: '8252 Hwy 23, Belle Chasse, LA 70037',
    parish: 'Plaquemines', lat: 29.846, lon: -89.988,
    capacityTotal: 800, hasFood: false, hasWater: true, hasCots: false,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-laplace-civic', name: 'LaPlace Civic Center',
    type: 'general', status: 'unknown', operator: 'parish',
    address: '188 Belle Terre Blvd, LaPlace, LA 70068',
    parish: 'St. John', lat: 30.068, lon: -90.471,
    capacityTotal: 1200, hasFood: false, hasWater: true, hasCots: true,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
  createShelter({
    id: 'shelter-kenner-convention', name: 'Pontchartrain Convention Center',
    type: 'general', status: 'unknown', operator: 'municipal',
    address: '4545 Williams Blvd, Kenner, LA 70065',
    parish: 'Jefferson', lat: 29.994, lon: -90.241,
    capacityTotal: 3000, hasFood: false, hasWater: true, hasCots: true,
    isADAAccessible: true, reportedAt: nowISO(),
  }),
];

const SHELTER_FS_KEYS = [
  'id','name','type','status','operator','address','parish','lat','lon',
  'capacityTotal','capacityCurrent',
  'isADAAccessible','acceptsPets','acceptsSpecialNeeds','hasMedicalStaff',
  'hasFood','hasWater','hasCots','hasAC','hasParking','hasBackupPower',
  'phone','openedAt','closedAt','hours','notes',
  'reportedAt','updatedAt','reportedBy','confirmCount','isOfficial','officialSourceId',
];

const SHELTER_MIGRATIONS = {
  // v0: 'capacity' was a string like "500". Coerce to number.
  0: (d) => {
    if (typeof d.capacity === 'string') {
      d.capacityTotal = parseInt(d.capacity, 10) || null;
      delete d.capacity;
    }
    return d;
  },
};

export const ShelterConverter = {
  toFirestore:   (s) => toFirestoreClean(s, SHELTER_FS_KEYS),
  fromFirestore: (snap) => fromFirestoreNormalize(snap.data(), createShelter, SHELTER_MIGRATIONS),
};
